import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { Main } from "./layout/Main";
import PrivateRoute from "./PrivateRoute";
// import BulkAnalyzer from "./pages/BulkAnalyzer";
// import { BulkAnalyzerUploads } from "./pages/BulkAnalyzerUploads";
import Dashboard from "./pages/Dashboard";
import ViewCharts from "./pages/ViewCharts";
import History from "./pages/History";
import ProductAnalyzer from "./pages/ProductAnalyzer";
import Settings from "./pages/Settings";
import GeneralSettings from "./pages/Settings/General";
import ProductAnalyzerSettings from "./pages/Settings/ProductAnalyzer";
import SourcingSettings from "./pages/Settings/Sourcing";
import DisplaySettings from "./pages/Settings/Display";
import LayoutSettings from "./pages/Settings/Layout";
import MyDetails from "./pages/MyDetails";
import Billing from "./pages/Billing";
import { AuthWrapper } from "./components";
import SignUp from "./pages/SignUp";
import ResetPassword from "./pages/ResetPassword";
import SignIn from "./pages/SignIn";
import SetNewPassword from "./pages/SetNewPassword";
import EmailVerification from "./pages/EmailVerification";
import ExtensionActionRequired from "./pages/ExtensionActionRequired";
import Subscriptions from "./pages/Subscriptions";
import AccountSettingsWrapper from "./components/AccountSettingsWrapper";
import Search from "./pages/Search";
import TokenLogin from "./pages/TokenLogin";
import NewEmailVerification from "./pages/NewEmailVerification";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
  selectIsLoggedIn,
  selectUserProfile,
} from "./features/user/user.selector";
import {
  getSettingsThunk,
  getTagsThunk,
} from "./features/settings/settings.thunk";
import { getUserThunk } from "./features/user/user.thunk";
import { useEffect } from "react";
import { fetchPricingPlans } from "./features/pricingPlans/pricingPlans.thunk";
import { syncExtensionAuth } from "./utils/extension";
import { selectExtension } from "./features/extension/extension.selector";
// import SessionExpired from "./pages/SessionExpired";
import Signout from "./pages/Signout";
import AuthorizeAmazon from "./pages/AuthorizeAmazon";
import ChoosePlan from "./pages/ChoosePlan";
import PaymentSuccess from "./pages/PaymentSuccess";
import { LocalStorageKeys } from "./constants";
import { resetUserState } from "./features/user/user.slice";
// import DevLoginPage from "./pages/DevLogin";
import PromotionLayout from "./layout/Promotion";
import AnnualOneTimeOffer from "./pages/Promotions/AnnualOneTimeOffer";
import ExtensionSignupOnboarding from "./pages/Promotions/ExtensionSignupOnboarding";
import { useAnalytics } from "./hooks/useAnalytics";
import WebSignupOnboarding from "./pages/Promotions/WebSignupOnboarding";
import { fetchSubscriptionCoupons } from "./features/subscriptionCoupons/subscriptionCoupons.thunk";
// import {
//   selectCoupons,
//   selectCouponUsages,
//   selectIsLoadingCoupons,
// } from "./features/subscriptionCoupons/subscriptionCoupons.selector";

function App() {
  const location = useLocation();
  const currentPath = location.pathname;
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const apiPassword = localStorage.getItem(LocalStorageKeys.API_PASSWORD);
  const { trackChromeInstall } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();

  const gaTrackExtensionInstall = (userId?: string) => {
    if (
      searchParams.get("from") === "extension" &&
      searchParams.get("trackInstall") === "true" &&
      currentPath === "/signup"
    ) {
      trackChromeInstall({ userId });
      setSearchParams((searchParams) => {
        searchParams.delete("trackInstall");
        return searchParams;
      });
    }
  };

  // const coupons = useAppSelector(selectCoupons);
  // const couponUsages = useAppSelector(selectCouponUsages);
  // const isLoading = useAppSelector(selectIsLoadingCoupons);

  const user = useAppSelector(selectUserProfile);
  const onboardingState = user?.onboardingState;
  const subscriptionState = user?.subscriptionState;
  const { isExtension } = useAppSelector(selectExtension);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getTagsThunk());
      dispatch(getUserThunk())
        .unwrap()
        .then((user) => {
          gaTrackExtensionInstall(user.id);
        })
        .catch(() => {
          gaTrackExtensionInstall();
        });
      // dispatch(fetchSubscriptionCoupons());
      dispatch(getSettingsThunk());
      dispatch(fetchPricingPlans());
    } else {
      gaTrackExtensionInstall();
    }
  }, [isLoggedIn]);

  // Sync onboardingState and subscriptionState to extension when they change (only from website, not extension)
  useEffect(() => {
    if (!isExtension && isLoggedIn && (onboardingState !== undefined || subscriptionState !== undefined)) {
      syncExtensionAuth(false, onboardingState, subscriptionState);
    }
  }, [isExtension, isLoggedIn, onboardingState, subscriptionState]);

  // if (process.env.REACT_APP_ENV !== "production" && !apiPassword) {
  //   dispatch(resetUserState());
  //   return <DevLoginPage />;
  // }

  return (
    <Routes>
      {/* JWT auto-login from mobile app */}
      <Route path="/token-login" element={<TokenLogin />} />

      {/* Auth */}
      <Route path="/email-change-verify" element={<NewEmailVerification />} />

      <Route element={<AuthWrapper />}>
        <Route index path="/login" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ResetPassword />} />
        <Route path="/reset-password" element={<SetNewPassword />} />
        <Route path="/email-verify" element={<EmailVerification />} />
        {/* <Route path="/extension-action-required" element={<ExtensionActionRequired />} /> */}
        {/* <Route path="/session-expired" element={<SessionExpired />} /> */}
      </Route>

      <Route element={<PrivateRoute />}>
        <Route path="/logout" element={<Signout />} />
        <Route path="/choose-plan" element={<ChoosePlan />} />
        <Route element={<PromotionLayout />}>
          <Route path="/annual-one-time-offer" element={<AnnualOneTimeOffer />}>
            <Route index element={<></>} />
            <Route path="payment-success" element={<PaymentSuccess />} />
          </Route>
          <Route
            path="/extension-signup-onboarding"
            element={<ExtensionSignupOnboarding />}
          />
          <Route path="/onboarding" element={<WebSignupOnboarding />} />
        </Route>
        <Route element={<Main />}>
          <Route index element={<Navigate to="product-analyzer" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/product-analyzer" element={<ProductAnalyzer />}>
            <Route index element={<></>} />
            <Route path="payment-success" element={<PaymentSuccess />} />
          </Route>
          <Route path="/search" element={<Search />} />
          <Route path="/history" element={<History />} />
          {/* <Route path="/bulk-analyzer" element={<BulkAnalyzerUploads />} /> */}
          {/* <Route
            path="/uploads"
            element={<Navigate to="/bulk-analyzer#uploads" />}
          /> */}
          {/* <Route path="/bulk-analyzer/:id" element={<BulkAnalyzer />} /> */}
          <Route path="/settings" element={<Settings />}>
            <Route index element={<Navigate to="sourcing" replace />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="sourcing" element={<SourcingSettings />} />
            <Route path="display" element={<DisplaySettings />} />
            <Route path="layout" element={<LayoutSettings />} />
            <Route
              path="product-analyzer"
              element={<ProductAnalyzerSettings />}
            />
            {/* <Route path="bulk-analyzer" element={<div></div>} /> */}
            {/* <Route path="amazon-credentials" element={<AmazonCredentials />} /> */}
          </Route>
          <Route path="/account" element={<AccountSettingsWrapper />}>
            <Route index element={<MyDetails />} />
            <Route path="billing" element={<Billing />} />
            <Route path="subscriptions" element={<Subscriptions />}>
              <Route index element={<></>} />
              <Route path="payment-success" element={<PaymentSuccess />} />
            </Route>
          </Route>
          <Route path="/view-charts" element={<ViewCharts />} />
          <Route path="/authorize/amazon" element={<AuthorizeAmazon />} />
        </Route>
      </Route>

      {/* Handle 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
